import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed } from '@fortawesome/free-solid-svg-icons';
import { faFileLines, faImage, faMap } from '@fortawesome/free-regular-svg-icons';
import HotelGallery from './HotelGallery';
import { useNavigate } from 'react-router-dom';
import RoomsDetails from './RoomsDetails';
import DetailsMap from '../Details/DetailsMap';
import HotelSummary from './HotelSummary';
import { DealModel, PckRoom } from 'ts/package/interfaces/PackageOfflineResponse';
import Button from 'components/ui/Button/Button';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { isTT } from 'constants/generic';
import Tabs from 'components/ui/Tabs/Tabs';

interface HotelDetailsProps {
  dealModel: DealModel;
  onChangeRoom: (room: PckRoom, uKey: string) => void;
  selectedRoomKey: string;
  onClickBook: () => void;
}

const HotelDetails: FC<HotelDetailsProps> = ({ dealModel, ...props }) => {
  const [t] = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [numPax, setNumPax] = useState(1);

  const onClickBook = () => {
    const params = new URLSearchParams({
      name: dealModel.package.name.name,
      priceUkey: dealModel.room?.name
        ? props.selectedRoomKey
        : dealModel.package?.oneItemPrice?.uKey,
      searchToken: dealModel.searchToken,
      numPax:
        dealModel.package.productType === 'FLTM'
          ? numPax.toString()
          : dealModel.paxes.length.toString(),
    });
    props.onClickBook();
    navigate({ pathname: '/offline-package/book', search: params.toString() });
  };

  return (
    <Fragment>
      {dealModel.package.productType !== 'FLTM' ? (
        <div className="default-shadow flight-details-item">
          <Tabs
            label={t('hotel.desc')}
            value={String(activeTab)}
            onValueChange={(v) => setActiveTab(Number(v))}
            items={[
              {
                value: '0',
                label: (
                  <>
                    <FontAwesomeIcon icon={faBed} className="mr-5" />
                    {t('hotel.rooms_prices', {
                      count: dealModel.package.oneItemPrice?.listOfpckRoomTypeList?.length,
                    })}
                  </>
                ),
              },
              {
                value: '1',
                label: (
                  <>
                    <FontAwesomeIcon icon={faFileLines as any} className="mr-5" />
                    {t('hotel.desc')}
                  </>
                ),
              },
              {
                value: '2',
                label: (
                  <>
                    <FontAwesomeIcon icon={faImage as any} className="mr-5" />
                    {t('hotel.gallery')}
                  </>
                ),
              },
              {
                value: '3',
                label: (
                  <>
                    <FontAwesomeIcon icon={faMap as any} className="mr-5" />
                    {t('map.map')}
                  </>
                ),
              },
            ]}
          />

          {activeTab === 0 && (
            <RoomsDetails
              dealModel={dealModel}
              onChangeRoom={props.onChangeRoom}
              selectedRoomKey={props.selectedRoomKey}
            />
          )}
          {activeTab === 1 && <HotelSummary deal={dealModel} />}
          {activeTab === 2 && <HotelGallery images={dealModel.package.images.getImages} />}
          {activeTab === 3 && (
            <DetailsMap
              lat={+dealModel.package.geoCode.latitude}
              lng={+dealModel.package.geoCode.longitude}
              zoom={17}
            />
          )}
        </div>
      ) : (
        <div className="default-shadow flight-details-item ">
          <div className="row">
            <div className="col-md-6 col-12">
              <div className="mb-5">{t('package.select_num_pax')}</div>
              <SelectCustom
                onChange={setNumPax}
                value={numPax}
                options={[
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                  { label: '5', value: 5 },
                  { label: '6', value: 6 },
                ]}
                isDropdown={true}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex-row-end">
        <Button
          className="btn btn-primary btn-lg"
          title={t(isTT ? 'continue' : 'hotel.book')}
          onClick={onClickBook}
        />
      </div>
    </Fragment>
  );
};

export default withErrorBoundary(HotelDetails, 'HotelDetails');
